import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Upload, FileUp, Loader2, CheckCircle2, AlertCircle, RefreshCw,
} from "lucide-react";

/**
 * BulkDocsUploadDialog — Reutilizado pelos 3 módulos (Contrato, EPI, Crachá).
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (v: boolean) => void
 *  - kind: "contract" | "epi" | "badge"
 *  - adminToken: string
 *  - onConfirmed: (result: {created, skipped, errors_count}) => void
 *  - title: string (ex: "Importar Contratos do Folhamatic")
 *  - accentClassName: string (ex: "bg-blue-700 hover:bg-blue-800")
 */
export default function BulkDocsUploadDialog({
  open, onOpenChange, kind, adminToken, onConfirmed,
  title, accentClassName = "bg-slate-700 hover:bg-slate-800",
}) {
  const [phase, setPhase] = useState("upload"); // upload | review
  const [busy, setBusy] = useState(false);
  const [stagingId, setStagingId] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [activePage, setActivePage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [counters, setCounters] = useState({ total: 0, matched: 0, unmatched: 0 });
  const [contractTipo, setContractTipo] = useState("indeterminado");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPhase("upload"); setStagingId(""); setItems([]);
      setSelected(new Set()); setActivePage(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(""); setBusy(false);
      setContractTipo("indeterminado");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      if (kind === "contract") fd.append("tipo", contractTipo);
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/bulk-docs/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken}` },
        timeout: 120000,
      });
      const d = r.data;
      setStagingId(d.staging_id);
      setItems(d.items || []);
      setCounters({ total: d.total, matched: d.matched, unmatched: d.unmatched });
      // Pre-select all matched
      const preSel = new Set();
      (d.items || []).forEach((it) => {
        if (it.employee_exists) preSel.add(it.page_index);
      });
      setSelected(preSel);
      if ((d.items || []).length > 0) {
        setActivePage(d.items[0].page_index);
        loadPreview(d.staging_id, d.items[0].page_index);
      }
      setPhase("review");
      toast.success(`PDF analisado: ${d.matched}/${d.total} funcionários identificados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao processar PDF");
    } finally { setBusy(false); }
  };

  const loadPreview = async (sid, pageIdx) => {
    try {
      const r = await axios.get(
        `${API}/admin/bulk-docs/staging/${sid}/preview/${pageIdx}`,
        { headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob", timeout: 30000 },
      );
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch {
      toast.error("Falha ao carregar pré-visualização");
    }
  };

  const switchPreview = (pageIdx) => {
    setActivePage(pageIdx);
    if (stagingId) loadPreview(stagingId, pageIdx);
  };

  const toggleOne = (pageIdx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pageIdx)) next.delete(pageIdx);
      else next.add(pageIdx);
      return next;
    });
  };

  const toggleAllMatched = () => {
    const allMatched = items.filter((it) => it.employee_exists);
    const allSelected = allMatched.every((it) => selected.has(it.page_index));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allMatched.forEach((it) => next.delete(it.page_index));
      } else {
        allMatched.forEach((it) => next.add(it.page_index));
      }
      return next;
    });
  };

  const confirm = async () => {
    if (selected.size === 0) {
      return toast.error("Selecione ao menos 1 funcionário");
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/bulk-docs/staging/${stagingId}/confirm`,
        { page_indices: Array.from(selected) },
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 120000 },
      );
      const d = r.data;
      toast.success(`${d.created} documento(s) criado(s) com sucesso`);
      if (d.skipped) toast.info(`${d.skipped} pulado(s) (sem cadastro)`);
      if (d.errors_count) toast.error(`${d.errors_count} erro(s)`);
      onConfirmed && onConfirmed(d);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao confirmar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={phase === "review"
          ? "max-w-6xl w-[95vw] h-[92vh] flex flex-col"
          : "max-w-lg w-[95vw]"}
        data-testid={`bulk-docs-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5 text-slate-700" />
            {title || "Importar PDF do Folhamatic"}
          </DialogTitle>
          <DialogDescription>
            {phase === "upload" && "Envie o PDF gerado pelo Folhamatic (1 funcionário por página). O sistema parseia e gera o modelo moderno para cada um."}
            {phase === "review" && "Revise os funcionários identificados, marque/desmarque e confirme."}
          </DialogDescription>
        </DialogHeader>

        {phase === "upload" && (
          <div className="space-y-3 py-2">
            {kind === "contract" && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-semibold text-slate-700 mb-2">
                  Tipo de Contrato do Lote
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setContractTipo("indeterminado")}
                    data-testid="bulk-tipo-indeterminado"
                    className={`text-left rounded-md border p-2.5 transition ${
                      contractTipo === "indeterminado"
                        ? "border-sky-600 bg-white shadow-sm"
                        : "border-slate-200 bg-white/50 hover:bg-white"
                    }`}
                  >
                    <div className="text-xs font-semibold text-sky-800">
                      Prazo Indeterminado
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      CLT Art. 442 · 443 · Regra geral
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setContractTipo("experiencia")}
                    data-testid="bulk-tipo-experiencia"
                    className={`text-left rounded-md border p-2.5 transition ${
                      contractTipo === "experiencia"
                        ? "border-sky-600 bg-white shadow-sm"
                        : "border-slate-200 bg-white/50 hover:bg-white"
                    }`}
                  >
                    <div className="text-xs font-semibold text-sky-800">
                      Experiência
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      CLT Art. 443 §2º · Máx. 90 dias
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setContractTipo("determinado")}
                    data-testid="bulk-tipo-determinado"
                    className={`text-left rounded-md border p-2.5 transition ${
                      contractTipo === "determinado"
                        ? "border-sky-600 bg-white shadow-sm"
                        : "border-slate-200 bg-white/50 hover:bg-white"
                    }`}
                  >
                    <div className="text-xs font-semibold text-sky-800">
                      Prazo Determinado
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      CLT Art. 443 §2º · Lei 9.601/1998
                    </div>
                  </button>
                </div>
              </div>
            )}
            <button onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="w-full border-2 border-dashed border-slate-300 hover:border-slate-500 rounded-lg p-8 transition cursor-pointer flex flex-col items-center justify-center gap-2"
              data-testid={`bulk-docs-pick-${kind}`}>
              {busy ? (
                <Loader2 className="w-10 h-10 text-slate-400 animate-spin" />
              ) : (
                <Upload className="w-10 h-10 text-slate-400" />
              )}
              <div className="text-sm font-medium text-slate-700">
                {busy ? "Processando…" : "Clique para selecionar o PDF"}
              </div>
              <div className="text-xs text-slate-500">
                Suporta vários funcionários no mesmo arquivo (1 por página)
              </div>
            </button>
            <input ref={fileRef} type="file" accept="application/pdf"
              className="hidden"
              onChange={(e) => doUpload(e.target.files?.[0])}
              data-testid={`bulk-docs-file-${kind}`} />
          </div>
        )}

        {phase === "review" && (
          <div className="flex gap-3 flex-1 min-h-0">
            {/* Left: identified list */}
            <div className="w-[340px] flex-shrink-0 flex flex-col gap-2 min-h-0">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-700">
                    {counters.total} encontrado(s)
                  </span>
                  <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                    {counters.matched} com cadastro
                  </Badge>
                </div>
                {counters.unmatched > 0 && (
                  <div className="mt-1 text-[10px] text-amber-700">
                    {counters.unmatched} sem cadastro no sistema (serão ignorados)
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-2">
                <button onClick={toggleAllMatched}
                  className="text-xs text-blue-700 hover:underline"
                  data-testid={`bulk-docs-toggle-all-${kind}`}>
                  Selecionar todos
                </button>
                <span className="text-xs font-bold text-slate-700">
                  {selected.size} marcado(s)
                </span>
              </div>

              <div className="flex-1 overflow-y-auto rounded-md border border-slate-200 bg-white">
                {items.map((it) => (
                  <button key={it.page_index}
                    onClick={() => switchPreview(it.page_index)}
                    className={`w-full text-left p-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition ${
                      activePage === it.page_index ? "bg-blue-50" : ""
                    }`}
                    data-testid={`bulk-docs-row-${it.page_index}`}>
                    <div className="flex items-start gap-2">
                      <Checkbox
                        checked={selected.has(it.page_index)}
                        disabled={!it.employee_exists}
                        onCheckedChange={() => toggleOne(it.page_index)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-slate-900 truncate">
                          {it.employee_nome || "—"}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono">
                          Mat. {it.employee_matricula || "—"}
                          {it.employee_cpf
                            ? ` · CPF ${it.employee_cpf.slice(0, 3)}.***.${it.employee_cpf.slice(-2)}`
                            : ""}
                        </div>
                        <div className="mt-1">
                          {it.employee_exists ? (
                            <Badge variant="outline" className="text-[9px] text-emerald-700 border-emerald-300 px-1 py-0">
                              <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                              {it.match_key}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300 px-1 py-0">
                              <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                              Sem cadastro
                            </Badge>
                          )}
                          {it.items_count != null && (
                            <Badge variant="outline" className="text-[9px] text-slate-700 ml-1 px-1 py-0">
                              {it.items_count} EPIs
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right: PDF preview */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs flex items-center justify-between">
                <span className="text-slate-600">
                  Pré-visualização: <strong>{
                    items.find((it) => it.page_index === activePage)?.employee_nome || "—"
                  }</strong>
                </span>
                <Button size="sm" variant="outline"
                  onClick={() => activePage != null && loadPreview(stagingId, activePage)}>
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Recarregar
                </Button>
              </div>
              {previewUrl ? (
                <iframe src={previewUrl} title="preview"
                  className="flex-1 w-full border border-slate-200 rounded-md" />
              ) : (
                <div className="flex-1 flex items-center justify-center bg-slate-50 rounded-md">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-slate-200 pt-3 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            disabled={busy}>
            Cancelar
          </Button>
          {phase === "review" && (
            <Button onClick={confirm} disabled={busy || selected.size === 0}
              className={`${accentClassName} text-white`}
              data-testid={`bulk-docs-confirm-${kind}`}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Criar {selected.size} pendente(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
